import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material'
import Swal from 'sweetalert2'
import { useNavigate, useLocation } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import TableShared from '../../components/TableShared'
import AddRoutingOperation from '../../components/routings/AddRoutingOperation'
import { isValid, handleAxiosError } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'

const CreateRouting = () => {
    const { t } = useTranslation()
    const { company_id, config, choosesite, setCompany_id, setChoosesite } = useStateContext()
    const navigate = useNavigate()
    const location = useLocation()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [statusOptions, setStatusOptions] = useState([])
    const [workCenters, setWorkCenters] = useState([])
    const [operationOptions, setOperationOptions] = useState([])
    const [rateUomOptions, setRateUomOptions] = useState([])

    /* form */
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [description, setDescription] = useState('')
    const [status, setStatus] = useState('')

    const [operations, setOperations] = useState([])
    const [openOperation, setOpenOperation] = useState(false)
    const [editingOperation, setEditingOperation] = useState(null)

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/routings')
    }

    const isFormValid = () => (
        isValid(code)
        && isValid(name)
        && isValid(siteId)
        && isValid(status)
    )

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
    }, [])

    useEffect(() => {
        if (!isValid(choosesite)) {
            setChoosesite(localStorage.getItem('site'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getSites(company_id)
        }
    }, [company_id])

    useEffect(() => {
        getStatusOptions()
    }, [])

    /* default the site to the user's current site once the options load, unless the user already picked one */
    useEffect(() => {
        if (!isValid(siteId) && sites.length > 0) {
            const defaultSiteId = choosesite || localStorage.getItem('site')
            const defaultSite = sites.find(site => String(site.id) === String(defaultSiteId))

            if (defaultSite) {
                setSiteId(defaultSite.id)
                setSiteName(defaultSite.name)
            }
        }
    }, [sites, choosesite])

    useEffect(() => {
        getOperationNames()
    }, [])

    useEffect(() => {
        getProductionRateUom()
    }, [])

    /* default the status to "Active" once the options load, unless the user already picked one */
    useEffect(() => {
        if (!isValid(status) && statusOptions.length > 0) {
            const activeStatus = statusOptions.find(o => String(o.value).toLowerCase() === 'active')
            setStatus(activeStatus?.value ?? statusOptions[0].value)
        }
    }, [statusOptions])

    /* work centers available for operations = active work centers belonging to the routing's site */
    useEffect(() => {
        setOperations([])
        if (isValid(siteId)) {
            getWorkCenters(siteId)
        } else {
            setWorkCenters([])
        }
    }, [siteId])

    const getSites = async (id) => {
        await axios
            .get(`/api/sites?company_id=${id}`, config)
            .then(res => {
                setSites(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/routing-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getOperationNames = async () => {
        await axios
            .get('/api/routing-operation-names', config)
            .then(res => {
                setOperationOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getProductionRateUom = async () => {
        await axios
            .get('/api/production-rate-uom', config)
            .then(res => {
                setRateUomOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getWorkCenters = async (site) => {
        await axios
            .get(`/api/work-centers?site_id=${site}`, config)
            .then(res => {
                const activeWorkCenters = (res.data?.data ?? []).filter(wc => String(wc.status).toLowerCase() === 'active')
                setWorkCenters(activeWorkCenters)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const handleSiteChange = (a, b) => {
        setSiteId(b?.id || '')
        setSiteName(b?.label || '')
    }

    const handleOpenOperation = () => {
        setEditingOperation(null)
        setOpenOperation(!openOperation)
    }

    const handleEditOperation = (row) => {
        setEditingOperation(row)
        setOpenOperation(true)
    }

    const deleteOperation = (id) => {
        setOperations((prev) => prev.filter((row) => row.id !== id))
    }

    const createRouting = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        const formData = {
            /* routing_code: code, */
            routing_name: name,
            site_id: siteId,
            description: description,
            status: status,
            routing_operations: operations.map((op) => ({
                sequence: op.sequence,
                operation_name: op.operation,
                work_center_id: op.workCenterId,
                production_rate: op.productionRate,
                production_rate_uom: op.rateUom,
                setup_time: op.setupTime
            }))
        }

        await axios.post('/api/routings', formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

            const createdId = data?.success?.data?.id
            if (createdId) {
                navigate(`/routings`, { state: { prevPathname: location.state?.prevPathname ?? '/routings' } })
            } else {
                goBack()
            }
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))

    const operationsColumns = [
        { field: 'sequence', headerName: t('sequence'), flex: 0.5, minWidth: 90 },
        {
            field: 'operation',
            headerName: t('operation'),
            flex: 1,
            minWidth: 130,
            valueFormatter: ({ value }) => operationOptions.find(o => o.value === value)?.label ?? value
        },
        { field: 'workCenterName', headerName: t('work_center'), flex: 1, minWidth: 150 },
        { field: 'productionRate', headerName: t('production_rate'), flex: 0.8, minWidth: 140 },
        {
            field: 'rateUom',
            headerName: t('rate_uom'),
            flex: 0.8,
            minWidth: 130,
            valueFormatter: ({ value }) => rateUomOptions.find(o => o.value === value)?.label ?? value
        },
        { field: 'setupTime', headerName: t('setup_time_hours'), flex: 0.8, minWidth: 150 },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.6,
            minWidth: 110,
            cellClassName: 'padding-0',
            renderCell: (params) => (
                <div className='flex justify-between'>
                    <Tooltip disableInteractive title={t('update')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => handleEditOperation(params.row)}><i className="fa-solid fa-pencil"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteOperation(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {t('create_routing')}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-5'>
                            {/* Code */}
                            {/* <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">Code *</p>
                                <TextField
                                    autoFocus
                                    placeholder="e.g. RT-001"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div> */}

                            {/* Name */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('name')} *</p>
                                <TextField
                                    placeholder=""
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Site */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                <Autocomplete
                                    disablePortal
                                    id="routing-site-autocomplete"
                                    options={optionsSites}
                                    value={siteId ? { label: siteName, id: siteId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={handleSiteChange}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Status */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('status')} *</p>
                                <Autocomplete
                                    disablePortal
                                    id="routing-status-autocomplete"
                                    options={statusOptions}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={statusOptions.find(o => o.value === status) || null}
                                    onChange={(a, b) => setStatus(b?.value || '')}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Description */}
                            <div className='w-full pb-4 lg:col-span-2'>
                                <p className="text-[14px] text-[#718096]">{t('description')}</p>
                                <TextField
                                    multiline
                                    rows={3}
                                    placeholder=""
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                        <div className='pt-1 pl-3 p-5'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('routing_operations')}</p>
                        </div>
                        <div className='flex justify-end p-5 pt-0 border-b'>
                            <Tooltip disableInteractive title={isValid(siteId) ? t('add_operation') : t('select_site_first')} placement='bottom'>
                                <div>
                                    <AddButton disabled={!isValid(siteId)} onClick={handleOpenOperation}><i className="fa-solid fa-plus"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>
                        <div className='border-b'>
                            {operations.length > 0 ? (
                                <div className='flex justify-end w-full p-2'>
                                    <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={() => setOperations([])}>{t('clear_all')}</button>
                                </div>
                            ) : null}
                            <TableShared columns={operationsColumns} items={operations} disableColumnMenu={true} />
                        </div>
                    </div>

                    <div className='w-full flex justify-end gap-3'>
                        <button type="button" onClick={createRouting} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                            <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                        </button>
                    </div>
                </div>

                <AddRoutingOperation
                    open={openOperation}
                    handleOpen={handleOpenOperation}
                    operations={operations}
                    setOperations={setOperations}
                    editingOperation={editingOperation}
                    workCenters={workCenters}
                    operationOptions={operationOptions}
                    rateUomOptions={rateUomOptions}
                />
            </Box>
        </>
    )
}

export default CreateRouting
